/**
 * Time utility functions for converting between minutes and human-readable format
 */

/**
 * Convert minutes to human-readable format (e.g., 90 -> "1h 30m")
 * @param {number} minutes - Total minutes
 * @returns {string} - Formatted time string
 */
export const minutesToTimeString = (minutes) => {
  if (minutes === undefined || minutes === null) return '';
  
  const numMinutes = Number(minutes);
  if (isNaN(numMinutes) || numMinutes < 0) return '';
  if (numMinutes === 0) return '';
  
  const hours = Math.floor(numMinutes / 60);
  const remainingMinutes = numMinutes % 60;
  
  let result = '';
  if (hours > 0) {
    result += `${hours}h`;
  }
  if (remainingMinutes > 0) {
    result += `${result ? ' ' : ''}${remainingMinutes}m`;
  }
  return result;
};

/**
 * Convert time string to minutes (e.g., "1h 30m" -> 90)
 * @param {string} timeString - Time string in format like "1h 30m", "2h", "45m"
 * @returns {number} - Total minutes
 */
export const timeStringToMinutes = (timeString) => {
  if (!timeString || typeof timeString !== 'string') return 0;
  
  const trimmed = timeString.trim().toLowerCase();
  if (!trimmed) return 0;
  
  let totalMinutes = 0;
  
  // Match hours (e.g., "2h", "1h")
  const hoursMatch = trimmed.match(/(\d+)h/);
  if (hoursMatch) {
    totalMinutes += parseInt(hoursMatch[1]) * 60;
  }
  
  // Match minutes (e.g., "30m", "45m")
  const minutesMatch = trimmed.match(/(\d+)m/);
  if (minutesMatch) {
    totalMinutes += parseInt(minutesMatch[1]);
  }
  
  return totalMinutes;
};

/**
 * Normalize time string by converting excess minutes to hours
 * @param {string} timeString - Time string to normalize
 * @returns {string} - Normalized time string
 */
export const normalizeTimeString = (timeString) => {
  if (!timeString || typeof timeString !== 'string') return '';
  
  const totalMinutes = timeStringToMinutes(timeString);
  return minutesToTimeString(totalMinutes);
};

/**
 * Validate time string format with 5-digit limits
 * @param {string} timeString - Time string to validate
 * @returns {boolean} - Whether the format is valid
 */
export const isValidTimeFormat = (timeString) => {
  if (!timeString || typeof timeString !== 'string') return true; // Empty is valid
  
  const trimmed = timeString.trim().toLowerCase();
  if (!trimmed) return true; // Empty is valid
  
  // Valid formats with max 5 digits for hours and minutes
  const timeRegex = /^(\d{1,5}h\s*)?(\d{1,5}m)?$/;
  return timeRegex.test(trimmed) && (trimmed.includes('h') || trimmed.includes('m'));
};

/**
 * Check if time string exceeds digit limits
 * @param {string} timeString - Time string to check
 * @returns {string} - Error message if exceeds limits, empty string if valid
 */
export const validateTimeDigitLimits = (timeString) => {
  if (!timeString || typeof timeString !== 'string') return '';
  
  const trimmed = timeString.trim().toLowerCase();
  if (!trimmed) return '';
  
  // Check for any sequence of more than 5 consecutive digits
  const digitSequences = trimmed.match(/\d+/g);
  if (digitSequences) {
    for (const sequence of digitSequences) {
      if (sequence.length > 5) {
        return 'Numbers cannot exceed 5 digits';
      }
    }
  }
  
  return '';
};

/**
 * Helper function to build a consistent ISO date-time string from separate date and time strings.
 * 
 * @param {string} dateStr - Date string (YYYY-MM-DD)
 * @param {string} timeStr - Time string (HH:MM)
 * @param {Object} options - Options object
 * @param {boolean} options.isStartDate - Whether this is a start date (defaults to false)
 * @param {boolean} options.isEditMode - Whether this is in edit mode (defaults to true)
 * @returns {string|null} - ISO string or null
 */
export const buildDateTimeString = (dateStr, timeStr, options = {}) => {
  const { isStartDate = false, isEditMode = true } = options;
  if (!dateStr) return null;
  if (!timeStr) {
    if (!isEditMode && isStartDate) {
      const now = new Date();
      timeStr = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    } else {
      timeStr = '00:00';
    }
  }
  
  try {
    const localDate = new Date(`${dateStr}T${timeStr}:00`);
    if (!isNaN(localDate.getTime())) {
      return localDate.toISOString();
    }
  } catch (e) {
    console.error(e);
  }
  
  // Fallback
  return null;
};

const formatLocalDate = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

/** Returns the browser-local date portion of a UTC timestamp. */
export const getLocalDatePart = (value) => {
  if (!value) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(String(value))) return String(value);
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : formatLocalDate(date);
};

/** Returns the browser-local HH:mm portion of a UTC timestamp. Midnight remains empty unless requested. */
export const getLocalTimePart = (value, options = {}) => {
  if (!value || !String(value).includes('T')) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  if (!options.includeMidnight && date.getHours() === 0 && date.getMinutes() === 0) return '';
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
};

export const formatDateWithOptionalTime = (value, hasTime = false, options = {}) => {
  if (!value) return options.fallback || '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return options.fallback || '';

  const dateText = date.toLocaleDateString(options.locale || undefined, {
    year: 'numeric',
    month: options.month || 'short',
    day: '2-digit',
  });

  if (!hasTime) return dateText;

  const timeText = date.toLocaleTimeString(options.locale || undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });

  return `${dateText}, ${timeText}`;
};

/** Detects legacy-vs-timed deadlines when API metadata is unavailable. */
export const hasStoredTimeComponent = (value) => {
  if (!value || !String(value).includes('T')) return false;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return false;
  // Date-only legacy tickets are stored at UTC midnight; any other UTC time means a due time exists.
  return (
    date.getUTCHours() !== 0 ||
    date.getUTCMinutes() !== 0 ||
    date.getUTCSeconds() !== 0 ||
    date.getUTCMilliseconds() !== 0
  );
};

/** Treats explicit due-time metadata as authoritative, with timestamp fallback for older responses. */
export const hasEffectiveDueTime = (hasDueTime, dueDate) => (
  Boolean(hasDueTime) || hasStoredTimeComponent(dueDate)
);

/** Applies exact timestamp comparison for timed tickets and end-of-day comparison for date-only tickets. */
export const isDueDateOverdue = (dueDate, hasDueTime = false, completed = false, nowValue = new Date()) => {
  if (!dueDate || completed) return false;

  const due = new Date(dueDate);
  const now = new Date(nowValue);
  if (Number.isNaN(due.getTime()) || Number.isNaN(now.getTime())) return false;

  if (hasDueTime) {
    return due.getTime() < now.getTime();
  }

  // Untimed due dates remain active until the local day has completely ended.
  const dueEndOfDay = new Date(due);
  dueEndOfDay.setHours(23, 59, 59, 999);
  return dueEndOfDay.getTime() < now.getTime();
};
/**
 * Parses a backend UTC timestamp for browser-local display.
 * @param {string} dateStr 
 * @returns {Date|null}
 */
export const parseBackendDate = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? null : d;
};
